import { msg } from 'gt-react';

export const TASK_CRUMB_FALLBACK = msg('Task', {
  context: 'breadcrumb fallback when a task title cannot be loaded',
});

export const TASKS_QUEUE_CRUMB = msg('Tasks', {
  context: 'breadcrumb ancestor linking back to the tasks queue',
});

export type CrumbAncestor = {
  title: string;
  path: string;
  search?: { fromPage: string } | { tab: string };
};

export type TaskDetailCrumbs = {
  crumb: string;
  crumbAncestors: CrumbAncestor[];
};

/**
 * Where "Tasks" goes for this user: the Tasks queue while the organization has
 * it (`tasks-enabled`), else the Inbox where assigned Tasks surface
 * (`inbox-enabled`), else the Tasks preview the top nav links to. The queue
 * route 404s when its flag is off, so it is never linked blind.
 */
export function tasksHomePath(flags: {
  tasksEnabled: boolean;
  inboxEnabled: boolean;
}): '/tasks' | '/inbox' | '/preview/tasks' {
  if (flags.tasksEnabled) return '/tasks';
  return flags.inboxEnabled ? '/inbox' : '/preview/tasks';
}

type TitledTask = { title?: string | null } | null | undefined;

function trimmedTitle(task: TitledTask): string | null {
  const title = task?.title?.trim();
  return title ? title : null;
}

/**
 * Breadcrumb trail for `/tasks/$taskId`, given whatever the loader managed
 * to fetch. Every input is optional so a failed lookup degrades to a
 * fallback instead of dropping the trail:
 *
 *   {page name | Tasks} > {parent task?} > {task title | Task}
 *
 * Titles stay encoded (`msg`) — the breadcrumb bar decodes them in the
 * active locale.
 */
export function buildTaskDetailCrumbs(input: {
  fromPage: string | undefined;
  /** The task page tab (task type key) the task was opened from. */
  fromPageTab?: string;
  task: TitledTask;
  /** Task page the user came from; null when there is none or it failed to load. */
  page: { name: string } | null | undefined;
  parentTaskId: string | null;
  /** Parent task; null when the task has none or it failed to load. */
  parent: TitledTask;
  /** The tasks view this user can reach (`tasksHomePath`). */
  tasksPath: string;
}): TaskDetailCrumbs {
  const ancestors: CrumbAncestor[] = [];

  ancestors.push(
    input.fromPage && input.page
      ? {
          title: input.page.name,
          path: `/pages/${input.fromPage}`,
          search: input.fromPageTab ? { tab: input.fromPageTab } : undefined,
        }
      : { title: TASKS_QUEUE_CRUMB, path: input.tasksPath },
  );

  if (input.parentTaskId && input.parent) {
    ancestors.push({
      title: trimmedTitle(input.parent) ?? TASK_CRUMB_FALLBACK,
      path: `/tasks/${input.parentTaskId}`,
      search: input.fromPage ? { fromPage: input.fromPage } : undefined,
    });
  }

  return {
    crumb: trimmedTitle(input.task) ?? TASK_CRUMB_FALLBACK,
    crumbAncestors: ancestors,
  };
}
